import { ReactNode } from 'react';
import { createRoot } from 'react-dom/client';

type MountArgs =
  // selector を渡した場合はその要素にマウントする
  | [selector: string, render: () => ReactNode]
  // 渡さない場合は body 直下に container を作ってマウントする。createPortal でページ内の
  // 複数要素へ配るときのように、マウント先が1つに決まらない用途で使う
  | [render: () => ReactNode | null];

type Target = {
  container: Element;
  node: ReactNode;
  ownsContainer: boolean;
};

const cleanups: (() => void)[] = [];

// Turbolinks は遷移時に body ごと差し替える。古い root を残すと、React が差し替え後に
// 古い DOM を消そうとして removeChild が失敗する（NotFoundError）。差し替え前に unmount する。
document.addEventListener('turbolinks:before-render', () => {
  cleanups.splice(0).forEach((cleanup) => cleanup());
});

// turbolinks:load は window の load 後（画像等の読み込み完了後）に発火する。初回表示を待たせたくないので
// DOMContentLoaded でもマウントし、両方が発火する初回ロードでは後から来た方を捨てる。
const register = (resolve: () => Target | null): void => {
  let mounted = false;

  const mount = () => {
    if (mounted) return;

    const target = resolve();
    if (!target) return;

    mounted = true;
    const root = createRoot(target.container);
    root.render(target.node);

    cleanups.push(() => {
      mounted = false;
      root.unmount();
      if (target.ownsContainer) target.container.remove();
    });
  };

  document.addEventListener('turbolinks:load', mount);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount);
  } else {
    mount();
  }
};

export const mountComponent = (...args: MountArgs): void =>
  register(() => {
    if (args.length === 2) {
      const [selector, render] = args;
      const elm = document.querySelector(selector);
      if (!elm) return null;

      return { container: elm, node: render(), ownsContainer: false };
    }

    const [render] = args;
    const node = render();
    if (node === null) return null;

    const container = document.createElement('div');
    document.body.appendChild(container);

    return { container, node, ownsContainer: true };
  });
